import { useCallback } from 'react';
import { StreamEvent } from '../../models/chatbot/types';
import { performAISummaryStream } from '../../services/ai-summary';

/**
 * Lightweight hook for streaming AI summary results
 *
 * This hook provides streaming functionality for AI summary. It does not manage
 * state internally. Instead, the component using this hook handles all state and
 * event processing via the onEvent callback.
 *
 * Primary Events:
 * - message_update → Text chunks to append to content
 * - stream_end → Stream completed successfully
 * - error → Stream error occurred
 *
 * @param programId - The program ID for the search
 * @returns Function to initiate streaming with custom event handler
 */
export const useAISummaryStreaming = (programId: string) => {
  const performSearch = useCallback(
    async (query: string, onEvent: (event: StreamEvent) => void) => {
      if (!programId) {
        console.error('No program ID provided for AI summary search');
        return;
      }

      try {
        performAISummaryStream({
          query,
          onEvent,
        });
      } catch (error) {
        console.error('AI summary streaming error:', error);
        onEvent({
          type: 'error',
          data: {
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
      }
    },
    [programId]
  );

  return performSearch;
};
